import React, { useState, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Search, 
  Calendar, 
  ChevronRight, 
  Filter, 
  X, 
  Image as ImageIcon,
  CheckCircle2,
  AlertCircle,
  Camera
} from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Checkbox } from '@/components/ui/checkbox'
import { useBGVGalleries, useBGVGalleryImages } from '@/hooks/useGalleryImages'
import { BGVGallery, GalleryAllImages, ReviewStatus, FranchiseRegion } from '@/types/gallery'
import { formatDate } from '@/lib/utils'

interface BGVGallerySelectorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onImagesSelected: (images: BGVSelectedImage[]) => void
  maxSelectionCount?: number
  initialSelectedImages?: BGVSelectedImage[]
}

export interface BGVSelectedImage {
  id: string
  url: string
  caption?: string
  gallery_id: number
  gallery_title: string
  alt_text?: string
  bgv_image_id: number
  bgv_image_type: string
}

interface GallerySelectionState {
  selectedGallery: BGVGallery | null
  selectedImages: BGVSelectedImage[]
  searchTerm: string
  reviewStatusFilter: ReviewStatus | 'all'
  regionFilter: FranchiseRegion | 'all'
  showFilters: boolean
}

export function BGVGallerySelector({
  open,
  onOpenChange,
  onImagesSelected,
  maxSelectionCount = 10,
  initialSelectedImages = []
}: BGVGallerySelectorProps) {
  const [state, setState] = useState<GallerySelectionState>({
    selectedGallery: null,
    selectedImages: initialSelectedImages,
    searchTerm: '',
    reviewStatusFilter: 'approved', // Default to approved galleries
    regionFilter: 'all',
    showFilters: false
  })

  const [activeTab, setActiveTab] = useState<'galleries' | 'images'>('galleries')

  // Fetch BGV galleries with filters
  const { 
    data: galleries, 
    isLoading: galleriesLoading, 
    error: galleriesError 
  } = useBGVGalleries({
    searchTerm: state.searchTerm,
    reviewStatus: state.reviewStatusFilter === 'all' ? undefined : state.reviewStatusFilter,
    franchiseRegion: state.regionFilter === 'all' ? undefined : state.regionFilter
  })

  // Fetch gallery images when a gallery is selected
  const { 
    data: galleryImages, 
    isLoading: imagesLoading,
    error: imagesError
  } = useBGVGalleryImages({
    galleryId: state.selectedGallery?.id,
    enabled: !!state.selectedGallery
  })

  const updateState = useCallback((updates: Partial<GallerySelectionState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }, [])

  const handleGallerySelect = useCallback((gallery: BGVGallery) => {
    updateState({ 
      selectedGallery: gallery,
      selectedImages: [] // Clear previous selections when changing galleries
    })
    setActiveTab('images')
  }, [updateState])

  const handleImageToggle = useCallback((image: GalleryAllImages) => {
    if (!state.selectedGallery) return

    const bgvImage: BGVSelectedImage = {
      id: `${image.image_type}-${image.original_id}`,
      url: image.image_url || '',
      caption: image.caption || undefined,
      gallery_id: image.gallery_id || 0,
      gallery_title: image.gallery_title || '',
      alt_text: image.caption || undefined,
      bgv_image_id: image.original_id || 0,
      bgv_image_type: image.image_type || 'gallery_photo'
    }

    const isSelected = state.selectedImages.some(img => img.id === bgvImage.id)
    
    if (isSelected) {
      // Remove image
      updateState({
        selectedImages: state.selectedImages.filter(img => img.id !== bgvImage.id)
      })
    } else {
      // Add image if under limit
      if (state.selectedImages.length < maxSelectionCount) {
        updateState({
          selectedImages: [...state.selectedImages, bgvImage]
        })
      }
    }
  }, [state.selectedGallery, state.selectedImages, maxSelectionCount, updateState])

  const handleConfirmSelection = useCallback(() => {
    if (state.selectedImages.length > 0) {
      onImagesSelected(state.selectedImages)
      onOpenChange(false)
    }
  }, [state.selectedImages, onImagesSelected, onOpenChange])

  const clearFilters = useCallback(() => {
    updateState({
      reviewStatusFilter: 'all',
      regionFilter: 'all',
      showFilters: false
    })
  }, [updateState])

  const clearSelection = useCallback(() => {
    updateState({ selectedImages: [] })
  }, [updateState])

  const isImageSelected = useCallback((image: GalleryAllImages) => {
    const imageId = `${image.image_type}-${image.original_id}`
    return state.selectedImages.some(img => img.id === imageId)
  }, [state.selectedImages])

  const isMaxReached = state.selectedImages.length >= maxSelectionCount

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[90vh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            {activeTab === 'galleries' 
              ? 'Select BGV Gallery' 
              : `Select Images from ${state.selectedGallery?.title}`
            }
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col h-full flex-1 overflow-hidden">
          <Tabs 
            value={activeTab} 
            onValueChange={(v) => setActiveTab(v as 'galleries' | 'images')} 
            className="flex-1 flex flex-col overflow-hidden"
          >
            <div className="px-6 border-b">
              <TabsList>
                <TabsTrigger value="galleries">BGV Galleries</TabsTrigger>
                <TabsTrigger value="images" disabled={!state.selectedGallery}>
                  Gallery Images
                  {state.selectedImages.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {state.selectedImages.length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-hidden">
              <TabsContent value="galleries" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col">
                <div className="flex-1 flex flex-col overflow-hidden">
                  {/* Gallery Search and Filters */}
                  <div className="p-6 border-b space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="relative flex-1">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search BGV galleries..."
                          className="pl-8"
                          value={state.searchTerm}
                          onChange={(e) => updateState({ searchTerm: e.target.value })}
                        />
                      </div>
                      <Popover open={state.showFilters} onOpenChange={(open) => updateState({ showFilters: open })}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2"
                          >
                            <Filter className="h-4 w-4" />
                            Filters
                            {(state.reviewStatusFilter !== 'all' || state.regionFilter !== 'all') && (
                              <Badge variant="secondary" className="ml-1">
                                {(state.reviewStatusFilter !== 'all' ? 1 : 0) + 
                                 (state.regionFilter !== 'all' ? 1 : 0)}
                              </Badge>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72">
                          <div className="space-y-4">
                            <div>
                              <Label className="text-sm font-medium">Review Status</Label>
                              <Select
                                value={state.reviewStatusFilter}
                                onValueChange={(value) => updateState({ reviewStatusFilter: value as ReviewStatus | 'all' })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All Statuses</SelectItem>
                                  <SelectItem value="approved">Approved</SelectItem>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="rejected">Rejected</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label className="text-sm font-medium">Franchise Region</Label>
                              <Select
                                value={state.regionFilter}
                                onValueChange={(value) => updateState({ regionFilter: value as FranchiseRegion | 'all' })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="all">All Regions</SelectItem>
                                  <SelectItem value="HQ">HQ</SelectItem>
                                  <SelectItem value="north_york">North York</SelectItem>
                                  <SelectItem value="london">London</SelectItem>
                                  <SelectItem value="toronto">Toronto</SelectItem>
                                  <SelectItem value="hamilton">Hamilton</SelectItem>
                                  <SelectItem value="mississauga">Mississauga</SelectItem>
                                  <SelectItem value="brampton">Brampton</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="flex justify-between pt-2">
                              <Button variant="outline" size="sm" onClick={clearFilters}>
                                Clear
                              </Button>
                              <Button size="sm" onClick={() => updateState({ showFilters: false })}>
                                Apply
                              </Button>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {galleriesError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          Failed to load BGV galleries. Please check your connection to BGV.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>

                  {/* Gallery List */}
                  <ScrollArea className="flex-1">
                    {galleriesLoading ? (
                      <div className="p-6 space-y-4">
                        {Array.from({ length: 6 }).map((_, i) => (
                          <div key={i} className="space-y-2">
                            <Skeleton className="h-4 w-3/4" />
                            <Skeleton className="h-3 w-1/2" />
                          </div>
                        ))}
                      </div>
                    ) : galleries && galleries.length === 0 ? (
                      <div className="p-6 text-center text-muted-foreground">
                        <ImageIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No BGV galleries found with the current filters</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {galleries?.map((gallery) => (
                          <div
                            key={gallery.id}
                            className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${
                              state.selectedGallery?.id === gallery.id ? 'bg-muted' : ''
                            }`}
                            onClick={() => handleGallerySelect(gallery)}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-medium truncate">{gallery.title}</h4>
                                  {state.selectedGallery?.id === gallery.id && (
                                    <ChevronRight className="h-4 w-4 text-primary flex-shrink-0" />
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                                  {gallery.description}
                                </p>
                                <div className="flex items-center gap-2 text-xs">
                                  <Badge 
                                    variant={gallery.review_status === 'approved' ? 'default' : 'secondary'}
                                    className="capitalize"
                                  >
                                    {gallery.review_status}
                                  </Badge>
                                  {gallery.franchise_region && (
                                    <Badge variant="outline">
                                      {gallery.franchise_region.replace('_', ' ')}
                                    </Badge>
                                  )}
                                  {gallery.job_number && (
                                    <span className="text-muted-foreground">
                                      Job #{gallery.job_number}
                                    </span>
                                  )}
                                  {gallery.created_on && (
                                    <span className="text-muted-foreground flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      {formatDate(gallery.created_on)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {gallery.cover_image && (
                                <img
                                  src={gallery.cover_image}
                                  alt={gallery.title}
                                  className="w-16 h-16 object-cover rounded-md ml-4 flex-shrink-0"
                                />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </TabsContent>

              <TabsContent value="images" className="h-full m-0 data-[state=active]:flex data-[state=active]:flex-col">
                {state.selectedGallery ? (
                  <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Gallery Info Header */}
                    <div className="p-6 border-b">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium">{state.selectedGallery.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            Select up to {maxSelectionCount} images for your social media post
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          onClick={() => setActiveTab('galleries')}
                        >
                          Change Gallery
                        </Button>
                      </div>

                      {imagesError && (
                        <Alert variant="destructive" className="mt-4">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            Failed to load gallery images. Please try again.
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>

                    {/* Images Grid */}
                    <ScrollArea className="flex-1 p-6">
                      {imagesLoading ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {Array.from({ length: 12 }).map((_, i) => (
                            <Skeleton key={i} className="w-full aspect-square rounded-md" />
                          ))}
                        </div>
                      ) : galleryImages && galleryImages.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-center">
                          <div>
                            <ImageIcon className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                            <p className="text-muted-foreground">
                              No images found in this gallery
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {galleryImages?.map((image) => {
                            const isSelected = isImageSelected(image)
                            const canSelect = !isMaxReached || isSelected
                            
                            return (
                              <div
                                key={`${image.image_type}-${image.original_id}`}
                                className={`
                                  relative rounded-lg overflow-hidden cursor-pointer border-2 group transition-all
                                  ${isSelected 
                                    ? 'border-primary ring-1 ring-primary' 
                                    : canSelect
                                    ? 'border-transparent hover:border-primary/50'
                                    : 'border-transparent opacity-50 cursor-not-allowed'
                                  }
                                `}
                                onClick={() => canSelect && handleImageToggle(image)}
                              >
                                <img
                                  src={image.image_url || ''}
                                  alt={image.caption || 'Gallery image'}
                                  className="w-full aspect-square object-cover transition-opacity"
                                />

                                <div className={`
                                  absolute inset-0 bg-black/20 flex items-center justify-center 
                                  transition-opacity
                                  ${isSelected 
                                    ? 'opacity-100' 
                                    : canSelect
                                    ? 'opacity-0 group-hover:opacity-70'
                                    : 'opacity-30'
                                  }
                                `}>
                                  {isSelected && (
                                    <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center">
                                      <CheckCircle2 className="h-5 w-5" />
                                    </div>
                                  )}
                                  {!canSelect && !isSelected && (
                                    <div className="bg-muted text-muted-foreground rounded-full w-8 h-8 flex items-center justify-center">
                                      <X className="h-5 w-5" />
                                    </div>
                                  )}
                                </div>

                                {isSelected && (
                                  <Badge className="absolute top-2 right-2 z-10">
                                    {state.selectedImages.findIndex(img => img.id === `${image.image_type}-${image.original_id}`) + 1}
                                  </Badge>
                                )}

                                {image.caption && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-2 text-xs">
                                    <p className="line-clamp-2">{image.caption}</p>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </ScrollArea>

                    {/* Selection Footer */}
                    <div className="border-t p-6 bg-muted/30">
                      <div className="flex justify-between items-center">
                        <div className="text-sm">
                          {isMaxReached && (
                            <span className="text-amber-600 font-medium">
                              Maximum selection reached ({maxSelectionCount} images)
                            </span>
                          )}
                          {!isMaxReached && state.selectedImages.length > 0 && (
                            <span>
                              Selected {state.selectedImages.length} of {maxSelectionCount} images
                            </span>
                          )}
                          {state.selectedImages.length === 0 && (
                            <span className="text-muted-foreground">
                              No images selected
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={clearSelection}
                            disabled={state.selectedImages.length === 0}
                          >
                            Clear Selection
                          </Button>
                          <Button
                            onClick={handleConfirmSelection}
                            disabled={state.selectedImages.length === 0}
                          >
                            Use Selected Images
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center flex-col p-6 text-center">
                    <div className="text-muted-foreground">
                      <ChevronRight className="h-16 w-16 mx-auto opacity-20 mb-4" />
                      <p>Select a gallery to view images</p>
                    </div>
                  </div>
                )}
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Dialog Footer */}
        <div className="border-t p-6 bg-background">
          <div className="flex justify-between items-center">
            <div className="text-sm text-muted-foreground">
              BGV Gallery Integration • {state.selectedImages.length} selected
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {state.selectedImages.length > 0 && (
                <Button onClick={handleConfirmSelection}>
                  Add {state.selectedImages.length} Image{state.selectedImages.length !== 1 ? 's' : ''}
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}